import { useStore } from 'effector-react';
import { sendEvent } from 'model/helpers/events.model';
import { FunctionalComponent, h } from 'preact';
import { styled } from 'linaria/react';
import { useEffect, useState } from 'preact/hooks';
import { localizationOptions$, updateLocalization } from 'model/localization.model';

export type LanguagePopupProps = {
	visible: boolean;
	onClose: () => void;
}

export const LanguagePopup: FunctionalComponent<LanguagePopupProps> = (props) => {
	const { visible, onClose } = props;
	const options = useStore(localizationOptions$);
	const [opacity, changeOpacity] = useState(0);
	const [display, changeDisplay] = useState(false);
	const languages = options.supportedLanguages ?
		Object
			.entries(options.supportedLanguages)
			.map(([code, name]) => ({ code, name })) :
		[];

	useEffect(() => {
		if (visible) {
			sendEvent({ event: 'language_select_opened' });
			changeDisplay(true);
			setTimeout(changeOpacity, 0, 1);
		} else {
			changeOpacity(0);
			setTimeout(changeDisplay, 200, false);
		}
	}, [visible]);
	const handleChangeLanguage = (code: string) => {
		sendEvent({
			event: 'language_selected',
			event_attributes: {
				previous: options.language,
				current: code,
			},
		});
		updateLocalization({ language: code });
	};

	return (
		<Overlay
			onClick={() => onClose()}
			style={{
				opacity,
				display: display ? 'flex' : 'none',
			}}
		>
			<Wrapper>
				{languages
					.filter(item => options.languagesWhitelist.includes(item.code))
					.map(item => (
						<LanguageItem onClick={() => handleChangeLanguage(item.code)}>
							<svg width="16" height="16" viewBox="0 0 16 16" fill="none" xmlns="http://www.w3.org/2000/svg">
								<path
									d="M8 0.25C3.71875 0.25 0.25 3.71875 0.25 8C0.25 12.2812 3.71875 15.75 8 15.75C12.2812 15.75 15.75 12.2812 15.75 8C15.75 3.71875 12.2812 0.25 8 0.25ZM12.8534 6.35344L7.10344 12.1034C7.00968 12.1971 6.88255 12.2498 6.75 12.2498C6.61745 12.2498 6.49032 12.1971 6.39656 12.1034L3.14656 8.85344C3.05287 8.75968 3.00023 8.63255 3.00023 8.5C3.00023 8.36745 3.05287 8.24032 3.14656 8.14656L3.85344 7.43937C3.89987 7.39293 3.95499 7.35609 4.01566 7.33096C4.07633 7.30582 4.14136 7.29289 4.20703 7.29289C4.2727 7.29289 4.33773 7.30582 4.3984 7.33096C4.45907 7.35609 4.51419 7.39293 4.56063 7.43937L6.75 9.62875L11.4394 4.93937C11.4858 4.89293 11.5409 4.85609 11.6016 4.83096C11.6623 4.80582 11.7273 4.79289 11.793 4.79289C11.8586 4.79289 11.9237 4.80582 11.9843 4.83096C12.045 4.85609 12.1001 4.89293 12.1466 4.93937L12.8534 5.64656C12.9471 5.74032 12.9998 5.86745 12.9998 6C12.9998 6.13255 12.9471 6.25968 12.8534 6.35344Z"
									fill={item.code === options.language ? 'var(--text-primary)' : 'transparent'}/>
								<path
									d="M7.10369 12.1035C7.00994 12.1972 6.88281 12.2498 6.75026 12.2498C6.61771 12.2498 6.49058 12.1972 6.39682 12.1035L3.14682 8.85351C3.05312 8.75975 3.00049 8.63262 3.00049 8.50007C3.00049 8.36752 3.05312 8.2404 3.14682 8.14664L3.85369 7.43945C3.90012 7.39301 3.95525 7.35617 4.01592 7.33103C4.07659 7.3059 4.14162 7.29296 4.20729 7.29296C4.27296 7.29296 4.33799 7.3059 4.39866 7.33103C4.45933 7.35617 4.51445 7.39301 4.56088 7.43945L6.75026 9.62882L11.4396 4.93946C11.4861 4.89301 11.5412 4.85617 11.6019 4.83104C11.6625 4.80591 11.7276 4.79297 11.7932 4.79297C11.8589 4.79297 11.9239 4.80591 11.9846 4.83104C12.0453 4.85617 12.1004 4.89301 12.1468 4.93946L12.8537 5.64664C12.9474 5.7404 13 5.86753 13 6.00008C13 6.13263 12.9474 6.25976 12.8537 6.35351L7.10369 12.1035Z"
									fill="transparent"/>
							</svg>
							{item.name}
						</LanguageItem>
					))}
			</Wrapper>
		</Overlay>
	);
};

const LanguageItem = styled('div')`
	display: flex;
	align-items: center;
	font-style: normal;
	font-weight: normal;
	font-size: 14px;
	line-height: 22px;
	color: var(--text-primary);
	transition: .2s ease-in-out;
	margin-bottom: 8px;
	cursor: pointer;
	
	svg {
		margin-right: 8px;
	}
	
	&:last-child {
		margin-bottom: 0;
	}
	
	&:hover {
		color: var(--text-secondary);
	}
`;
const Wrapper = styled('div')`
	z-index: 1;
	position: relative;
	max-width: 240px;
	width: 100%;
	padding: 12px;
	background: var(--background);
	box-shadow: 0 5px 15px rgba(0, 0, 0, 0.08), 0 0 0 1px rgba(136, 152, 170, 0.25), 0 15px 35px rgba(49, 49, 93, 0.15);
	border-radius: 4px;
`;
const Overlay = styled('div')`
	display: flex;
	justify-content: flex-start;
	align-items: flex-start;
	position: absolute;
	padding: 16px;
	left: 0;
	top: 0;
	bottom: 0;
	right: 0;
	background: transparent;
	border-radius: 4px;
	transition: opacity .2s ease-in-out;
`;
